import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { push, replace } from 'redux-first-history';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';

import styled from 'styled-components';
import { Paper } from '@dev/ee-components';
import { flatten, find, isNil, noop, omitBy } from 'lodash-es';
import { isEmpty } from 'lodash-es';
import { AccessLevel, CompressionFormat, ExportFileFormats, DeliveryLogType, MessageCategory } from '@dev/ee-api/ee-api-models';
import { ShowDialogParams } from '@api/store/dialog/types';
import { ApplicationState } from '@api/store';
import { LogTypesString, StdLoadLogsInterface } from '@api/store/logs/types';
import { ShowDialog } from '@api/store/dialog/actions';
import { LoadLogsStart, ExportLogs, ResendStart } from '@api/store/logs/actions';
import { ViewEmailStart } from '@api/store/emails/actions';
import { LoadSummaryStart, LoadSummaryStartParams, LoadBouncedSummary } from '@api/store/statistics/actions';
import EmailLogsInterface, { DateOptionsInterface } from './EmailLogInterface';
import { LogStatusesToDisplay } from '@api/services/dictionaries/Logs';
import dayjs from '@api/services/tools/dayjs';
import ExportModal from '../../../../ExportModal';
import { ExportLogsParams, ResendParams } from '@api/services/ApiCategories/logs';
import { debounce } from '@api/services/tools/debounce';
import { addParamToUrl, parseUrlParams } from '@api/services/tools/handleUrlParams';
import { local } from 'store2';

import { convertMessageCategoryAndStatuses } from '@api/components/Logs/helperFunctions';
import { LogsTable } from '@api/components/Logs/assets/LogsTable';
import { LogsFiltersBar } from '@api/components/Logs/assets/LogsFiltersBar';
import { ExtendedLogsBanner } from '@api/components/Logs/assets/ExtendedLogsBanner';
import { DEFAULT_PER_PAGE_COUNT, LIMITED_LOGS_DAYS, LIMITED_LOGS_DAYS_PRO } from '@api/services/constants';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

const MobileWrapper = styled.div`
    & div:last-child {
        & > div {
            // border-top: none;
            & .MuiPagination-root {
                margin-top: 8px;
            }
        }
    }
`;

const mapState = (state: ApplicationState, ownProps: EmailLogsInterface): EmailLogsInterface => {
    return {
        ...ownProps,
        browser: state.browser,
        logs: state.logs,
        //channels: state.channels,
        statistics: state.statistics,
        account: state.account,
    };
};

const dispatchProps = {
    LoadLogsStart,
    ViewEmailStart,
    ShowDialog,
    Push: push,
    Replace: replace,
    LoadSummaryStart,
    LoadBouncedSummary,
    ExportLogs,
    ResendStart,
};

type DispatchProps = {
    LoadLogsStart(params: StdLoadLogsInterface): void;
    ViewEmailStart(msgid: string): void;
    ShowDialog(params: ShowDialogParams): void;
    Push(url: string): void;
    Replace(param: Record<string, string> | string): void;
    LoadSummaryStart(params: LoadSummaryStartParams): void;
    LoadBouncedSummary(params: LoadSummaryStartParams): void;
    ExportLogs(params: ExportLogsParams): void;
    ResendStart: (params: ResendParams) => void;
};

const DATE_FORMAT = 'YYYY-MM-DD';

type Props = DispatchProps & EmailLogsInterface;

let extendedLogsStorage = local.get('showExtendLogsBanner');

class SingleChannelLogs extends Component<Props> {
    public state = {
        selectedResult: null,
        statuses: null,
        to: dayjs().utc().toJSON(),
        from: dayjs().subtract(this.getLimitedLogDays(), 'day').startOf('day').utc().toJSON(),
        perPage: DEFAULT_PER_PAGE_COUNT,
        exportOptions: {
            channelNames: [],
            fileName: null,
            compressionFormat: CompressionFormat.Zip,
            fileFormat: ExportFileFormats.Csv,
        },
        emailSearch: '',
        dateFilterValue: this.getLimitedLogDays(),
        activePage: 1,
        openEmailDetails: false,
        openEmailId: '',
        isLogBannerVisible: true,
    };

    private getLimitedLogDays(): number {
        const { GeneralInfo } = this.props?.account || {};
        return GeneralInfo?.KeepLogsForDays;
    }

    public componentDidMount(): void {
        let hideBanner = false;

        if (!extendedLogsStorage) {
            extendedLogsStorage = [];
        } else {
            if (typeof extendedLogsStorage !== 'object') {
                extendedLogsStorage = JSON.parse(extendedLogsStorage);
            }
            hideBanner = !!find(extendedLogsStorage, (el) => el === 'logBanner');
        }

        this.setState({ isLogBannerVisible: !hideBanner });

        if (this.loadLogsFromUrl()) {
            return;
        }

        return this.loadEmailsLogsData();
    }

    /**
     * seting state based on query string
     * params available on query string:
     * @param status
     * @param result
     * @param page
     */

    private loadLogsFromUrl(): boolean {
        const { location } = this.props;
        const query = parseUrlParams(location.search);
        if (isEmpty(query)) {
            return false;
        }

        const urlStatuses: string[] = query.statuses ? flatten([query.statuses]) : null;
        const urlResult: string[] = query.result ? flatten([query.result]) : null;
        let dateParams = {};
        if (query?.from && query?.to) {
            const from = dayjs.utc(query?.from as string);
            const to = dayjs.utc(query?.to as string);
            const dateDiff = to.diff(from, 'day');
            const dateOptions = this.getDateDropdownOptions();
            const selectedOption = dateOptions?.find((el) => el.value === dateDiff)?.value || 'calendar';
            dateParams = {
                from: from.toJSON(),
                to: to.toJSON(),
                dateFilterValue: selectedOption,
            };
        }

        const valuesBasedOnQuery = {
            statuses: urlStatuses,
            selectedResult: urlResult,
            emailSearch: query?.search,
            perPage: DEFAULT_PER_PAGE_COUNT,
            activePage: 1,
            ...dateParams,
        };

        this.setState({ ...valuesBasedOnQuery }, () => this.updateLogs());
        return true;
    }

    private changeExportCompression(enabled: boolean): void {
        const { exportOptions } = this.state;

        const updatedValues = {
            ...exportOptions,
            compressionFormat: enabled ? CompressionFormat.Zip : CompressionFormat.None,
        };
        this.setState({ exportOptions: updatedValues });
    }

    private changeExportFileFormat(format: ExportFileFormats): void {
        const { exportOptions } = this.state;
        this.setState({ exportOptions: { ...exportOptions, fileFormat: format } });
    }

    private singleChannelLogsFileNameRef = React.createRef<HTMLInputElement>();

    private exportInProgress(): void {
        const { ExportLogs, match } = this.props;
        const { exportOptions, from, to, statuses, selectedResult } = this.state;
        const fileName = this.singleChannelLogsFileNameRef.current.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;
        const [convertedStatuses, convertedResults] = convertMessageCategoryAndStatuses(statuses, selectedResult);

        const exportParams = {
            channelName: safelyDecodeBase64Url(match.params.channelName),
            fileName,
            statuses: isEmpty(convertedStatuses) ? [DeliveryLogType.All] : (convertedStatuses as DeliveryLogType[]),
            messageCategory: isEmpty(selectedResult) ? null : convertedResults,
            from,
            to,
            fileFormat,
            compressionFormat: exportOptions.compressionFormat,
        };
        const result = omitBy(exportParams, isNil) as unknown as ExportLogsParams;

        ExportLogs(result);
    }

    private openExportModal(): void {
        const { ShowDialog, t } = this.props;

        ShowDialog({
            title: t('export'),
            body: (
                <ExportModal
                    onCompressionChange={this.changeExportCompression.bind(this)}
                    onFileFormatChange={this.changeExportFileFormat.bind(this)}
                    inputRef={this.singleChannelLogsFileNameRef}
                />
            ),
            confirmLabel: t('export'),
            cancelLabel: t('cancel'),
            onClickConfirm: this.exportInProgress.bind(this),
        });
    }

    private loadEmailsLogsData(): void {
        const { LoadLogsStart, match } = this.props;
        const { to, from, statuses, perPage, selectedResult } = this.state;

        const params: Record<string, string | string[] | number | boolean> = {
            statuses: isEmpty(statuses) ? [DeliveryLogType.All] : statuses,
            inProgressLogPage: 1,
            channelName: safelyDecodeBase64Url(match.params.channelName),
            messageCategory: selectedResult ? [selectedResult] : null,
        };
        LoadLogsStart({ from, to, perPage, ...params });
    }

    public componentDidUpdate(prevProps): void {
        if (prevProps.clickExport !== this.props.clickExport && this.props.clickExport) {
            this.openExportModal();
        }

        this.props.disabledLogsExport(isEmpty(this.props.logs?.logList?.Recipients));
    }

    private updateFiltersResults(): void {
        const { to, from } = this.state;
        const { LoadSummaryStart, LoadBouncedSummary, match } = this.props;
        const filterParams: LoadSummaryStartParams = {
            from,
            to,
            channelName: safelyDecodeBase64Url(match.params.channelName),
        };

        LoadSummaryStart(filterParams);
        LoadBouncedSummary(filterParams);
    }

    private setDatesForLogs(event: any, selectedItem: Record<string, string>): void {
        const {} = this.state;
        const { value } = selectedItem;

        const to = dayjs().utc();
        const from = dayjs().utc().subtract(parseInt(value), 'day').startOf('day');

        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
        this.setState({ from: from.toJSON(), to: to.toJSON(), dateFilterValue: parseInt(value), activePage: 1 }, () => {
            this.updateLogs();
            this.updateFiltersResults();
        });
    }

    private setDatesBasedOnCalendar(fromTime: Date, toTime: Date): void {
        const from = dayjs(fromTime).utc().add(1, 'day').startOf('day');
        const to = dayjs(toTime).endOf('day').utc();
        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) });
        this.setState({ from: from.toString(), to: to.toString(), activePage: 1 }, () => {
            this.updateLogs();
            this.updateFiltersResults();
        });
    }

    private updateLogs(logLoading = true): void {
        const { LoadLogsStart, match } = this.props;
        const { to, from, selectedResult, perPage, emailSearch, activePage, statuses } = this.state;

        const [convertedStatuses, convertedResults] = convertMessageCategoryAndStatuses(statuses, selectedResult);

        const params: StdLoadLogsInterface = {
            from,
            to,
            statuses: isEmpty(convertedStatuses) ? [DeliveryLogType.All] : (convertedStatuses as any[]),
            channelName: safelyDecodeBase64Url(match.params.channelName),
            messageCategory: isEmpty(selectedResult) ? null : convertedResults,
            inProgressLogPage: activePage || 1,
            perPage,
            email: emailSearch,
            skipSagaURL: true,
            includeSms: false,
            includeEmail: true,
            logLoading,
        };
        LoadLogsStart(params);
    }

    /**
     * Function searches logs, condition is that email have to start with provided input
     * @param event input change event or string ( typeguard determines which it is tilll the mobile version will be re-done)
     */

    private isChangeEvent(ev: React.ChangeEvent<HTMLInputElement> | string): ev is React.ChangeEvent<HTMLInputElement> {
        return (ev as React.ChangeEvent<HTMLInputElement>)?.target !== undefined;
    }

    private searchLogsWhereEmailStartWith(event: React.ChangeEvent<HTMLInputElement> | string): void {
        const emailSearch = (this.isChangeEvent(event) ? event?.target?.value : event) || '';
        this.setState({ emailSearch }, () => {
            if (emailSearch) {
                debounce(() => {
                    this.addSearchQuery({ search: emailSearch });
                    this.updateLogs();
                }, 1000);
            } else {
                this.updateLogs();
                this.addSearchQuery({ search: emailSearch });
            }
        });
    }

    private canSeeExtendedLogs = () => {
        const { GeneralInfo } = this.props.account;
        return GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.ExtendedLogs);
    };

    private getDateDropdownOptions = (): DateOptionsInterface[] => {
        const { GeneralInfo } = this.props?.account || {};
        const hasExtendedLogs = this.canSeeExtendedLogs();
        const logsDays = GeneralInfo.KeepLogsForDays === LIMITED_LOGS_DAYS ? LIMITED_LOGS_DAYS : LIMITED_LOGS_DAYS_PRO;
        const options = [
            {
                borderBottom: true,
                label: this.props.i18n.t('lastdays', { days: logsDays }),
                value: logsDays,
            },
        ];

        if (hasExtendedLogs) {
            options.push(
                {
                    borderBottom: false,
                    label: this.props.i18n.t('lastdays', { days: 14 }),
                    value: 14,
                },
                {
                    borderBottom: false,
                    label: this.props.i18n.t('lastdays', { days: 35 }),
                    value: 35,
                }
            );
        }

        return [
            ...options,
            {
                borderBottom: false,
                label: this.props.i18n.t('specificdaterange'),
                value: 'calendar',
            },
        ];
    };

    private renderOptionBar(): any {
        const { emailSearch, dateFilterValue } = this.state;

        return (
            <LogsFiltersBar
                emailSearch={emailSearch}
                dateFilterValue={dateFilterValue}
                onClicks={{
                    refresh: this.updateLogs.bind(this),
                    dateFilter: this.setDatesForLogs.bind(this),
                    calendar: this.setDatesBasedOnCalendar.bind(this),
                    search: this.searchLogsWhereEmailStartWith.bind(this),
                }}
                onFilterChange={noop}
                availableFilters={{
                    date: true,
                    search: true,
                    channel: false,
                    campaign: false,
                }}
                logTypeScreen={LogTypesString.EMAIL}
                onMobileFilterChange={this.loadLogsFromUrl.bind(this)}
            />
        );
    }

    /**
     * function splits selected event filters from table headers to statuses and message category.
     * as the filter combines two separates api parameters
     */

    private splitSelectedItemsIntoProperState(arrayToSplit: (DeliveryLogType | MessageCategory)[]) {
        return arrayToSplit?.reduce(
            ([statuses, messageCategory], el) => {
                if (Object.keys(LogStatusesToDisplay).includes(el)) {
                    return [[...statuses, el], messageCategory];
                }
                return [statuses, [...messageCategory, el]];
            },
            [[], []]
        );
    }

    private onStatusFilterClick(event: React.SyntheticEvent<HTMLInputElement>, selectedItems: (DeliveryLogType | MessageCategory)[]): void {
        const [statuses, selectedResult] = this.splitSelectedItemsIntoProperState(selectedItems);

        this.addSearchQuery({ statuses, result: selectedResult });
        this.setState({ statuses, selectedResult, activePage: 1 }, (): void => {
            this.updateLogs();
        });
    }

    private addSearchQuery(toAdd: Record<string, any>): void {
        const { Replace } = this.props;
        const newPath = addParamToUrl(toAdd);
        Replace({ search: newPath });
    }

    private fetchNextOnScroll(): void {
        this.setState(
            (prevState: typeof this.state) => ({ activePage: prevState.activePage + 1 }),
            () => this.updateLogs(false)
        );
    }

    private isMobile(): boolean {
        const {
            browser: {
                lessThan: { hd },
            },
        } = this.props;
        return hd;
    }

    render(): JSX.Element {
        const { statuses, selectedResult } = this.state;
        const isMobile = this.isMobile();

        const selectedCheckboxes = [...(statuses ?? []), ...(selectedResult ?? [])];
        const TableWrapper = isMobile ? MobileWrapper : Paper;
        return (
            <>
                <ExtendedLogsBanner />
                <TableWrapper margin={{ top: 32 }}>
                    {this.renderOptionBar()}
                    <LogsTable
                        onHeadersEventFilterClick={this.onStatusFilterClick.bind(this)}
                        selectedEventsCheckboxes={selectedCheckboxes}
                        fetchNextOnScroll={this.fetchNextOnScroll.bind(this)}
                    />
                </TableWrapper>
            </>
        );
    }
}

export default compose(
    withRouter,
    connect<EmailLogsInterface, DispatchProps>(mapState, dispatchProps),
    withTranslation()
)(SingleChannelLogs);
